import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Chip,
  Switch,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
} from '@mui/material';
import {
  Search,
  Delete,
  Edit,
  Add,
  Refresh,
  ContentCopy,
  Business,
  Person,
  VpnKey,
  Casino,
  HelpOutline,
} from '@mui/icons-material';
import {
  getTestAccounts,
  createTestAccount,
  updateTestAccount,
  deleteTestAccount,
} from '../services/testAccounts';

export default function TestAccounts() {
  const [tabValue, setTabValue] = useState(0); // 0: Merchants, 1: Consumers
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog states
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    phoneNumber: '',
    role: 'merchant',
    otp: '',
    name: '',
    description: '',
  });

  // Feedback states
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTestAccounts();
      if (res && res.data) {
        setAccounts(res.data);
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load test accounts: ' + err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    showSnackbar(`Copied ${label} (${text}) to clipboard!`, 'info');
  };

  const generateRandomOtp = () => {
    const randomOtp = Math.floor(100000 + Math.random() * 900000).toString();
    setFormData((prev) => ({ ...prev, otp: randomOtp }));
  };

  const handleOpenAdd = () => {
    const defaultRole = tabValue === 0 ? 'merchant' : 'user';
    setFormData({
      phoneNumber: '',
      role: defaultRole,
      otp: Math.floor(100000 + Math.random() * 900000).toString(),
      name: '',
      description: '',
    });
    setOpenAddDialog(true);
  };

  const handleOpenEdit = (account) => {
    setSelectedAccount(account);
    setFormData({
      phoneNumber: account.phoneNumber ? account.phoneNumber.replace(/^\+91/, '') : '',
      role: account.role,
      otp: account.otp,
      name: account.name,
      description: account.description || '',
      isActive: account.isActive,
    });
    setOpenEditDialog(true);
  };

  const handleOpenDelete = (account) => {
    setSelectedAccount(account);
    setOpenDeleteDialog(true);
  };

  const handleSubmitAdd = async () => {
    if (!formData.phoneNumber || !formData.otp || !formData.name) {
      showSnackbar('Please fill all required fields (Phone, OTP, Name)', 'warning');
      return;
    }

    try {
      await createTestAccount(formData);
      showSnackbar('Test account created successfully!', 'success');
      setOpenAddDialog(false);
      fetchAccounts();
    } catch (err) {
      showSnackbar('Error creating test account: ' + err.message, 'error');
    }
  };

  const handleSubmitEdit = async () => {
    if (!selectedAccount) return;
    try {
      await updateTestAccount(selectedAccount.id, formData);
      showSnackbar('Test account updated successfully!', 'success');
      setOpenEditDialog(false);
      fetchAccounts();
    } catch (err) {
      showSnackbar('Error updating test account: ' + err.message, 'error');
    }
  };

  const handleToggleStatus = async (account) => {
    try {
      await updateTestAccount(account.id, { isActive: !account.isActive });
      showSnackbar(`Test account ${account.isActive ? 'deactivated' : 'activated'}!`, 'success');
      fetchAccounts();
    } catch (err) {
      showSnackbar('Failed to update status: ' + err.message, 'error');
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedAccount) return;
    try {
      await deleteTestAccount(selectedAccount.id);
      showSnackbar('Test account deleted successfully!', 'success');
      setOpenDeleteDialog(false);
      fetchAccounts();
    } catch (err) {
      showSnackbar('Error deleting test account: ' + err.message, 'error');
    }
  };

  const currentRole = tabValue === 0 ? 'merchant' : 'user';

  const filteredAccounts = accounts.filter((acc) => {
    const matchesRole = acc.role === currentRole;
    const cleanPhone = acc.phoneNumber ? acc.phoneNumber.replace(/^\+91/, '') : '';
    const matchesSearch =
      acc.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      acc.phoneNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cleanPhone.includes(searchTerm.toLowerCase()) ||
      acc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      acc.otp?.includes(searchTerm);
    return matchesRole && matchesSearch;
  });

  const merchantCount = accounts.filter((a) => a.role === 'merchant').length;
  const consumerCount = accounts.filter((a) => a.role === 'user').length;

  return (
    <Box sx={{ width: '100%' }}>
      {/* Top Banner / Card */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(82, 173, 131, 0.08) 0%, rgba(61, 143, 100, 0.04) 100%)',
          border: '1px solid rgba(82, 173, 131, 0.2)',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 2,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <VpnKey sx={{ color: '#52ad83', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937' }}>
              Test Accounts & OTP Bypass Management
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#6b7280' }}>
            Configure development & review phone numbers with fixed, unique OTPs. All numbers listed here bypass real SMS charges (0 INR) and authenticate directly.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchAccounts}
            sx={{
              borderColor: '#52ad83',
              color: '#52ad83',
              textTransform: 'none',
              borderRadius: 2,
              '&:hover': { borderColor: '#3d8f64', background: 'rgba(82,173,131,0.05)' },
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenAdd}
            sx={{
              background: 'linear-gradient(135deg, #52ad83 0%, #3d8f64 100%)',
              color: '#ffffff',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(82, 173, 131, 0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #449973 0%, #337a54 100%)' },
            }}
          >
            Add Test Account
          </Button>
        </Box>
      </Paper>

      {/* Tabs and Search Controls */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid rgba(0,0,0,0.08)',
          overflow: 'hidden',
          background: '#ffffff',
        }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 2,
          }}
        >
          <Tabs
            value={tabValue}
            onChange={(e, v) => {
              setTabValue(v);
              setPage(0);
            }}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                minHeight: 48,
                px: 2.5,
                '&.Mui-selected': { color: '#52ad83' },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#52ad83',
                height: 3,
                borderRadius: 2,
              },
            }}
          >
            <Tab
              icon={<Business sx={{ fontSize: 20 }} />}
              iconPosition="start"
              label={`Merchants (${merchantCount})`}
            />
            <Tab
              icon={<Person sx={{ fontSize: 20 }} />}
              iconPosition="start"
              label={`Consumers (${consumerCount})`}
            />
          </Tabs>

          <TextField
            size="small"
            placeholder="Search by phone, name, or OTP..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#9ca3af', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', sm: 300 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&.Mui-focused fieldset': { borderColor: '#52ad83' },
              },
            }}
          />
        </Box>

        {/* Table Content */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8 }}>
            <CircularProgress sx={{ color: '#52ad83' }} />
          </Box>
        ) : filteredAccounts.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: '#6b7280', mb: 1 }}>
              No test accounts found
            </Typography>
            <Typography variant="body2" sx={{ color: '#9ca3af', mb: 3 }}>
              {searchTerm
                ? 'Try clearing your search query'
                : `No ${currentRole === 'merchant' ? 'merchant' : 'consumer'} test accounts created yet.`}
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenAdd}
              sx={{
                background: '#52ad83',
                textTransform: 'none',
                borderRadius: 2,
                '&:hover': { background: '#3d8f64' },
              }}
            >
              Add First Test Account
            </Button>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead sx={{ background: '#f9fafb' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#4b5563' }}>Account Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#4b5563' }}>Phone Number</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#4b5563' }}>Fixed OTP</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#4b5563' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#4b5563' }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#4b5563' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAccounts
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((acc) => (
                      <TableRow
                        key={acc.id}
                        hover
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          transition: 'background 0.2s',
                        }}
                      >
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                            {acc.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#374151' }}
                            >
                              {acc.phoneNumber ? acc.phoneNumber.replace(/^\+91/, '') : ''}
                            </Typography>
                            <Tooltip title="Copy 10-digit Phone Number (for Login)">
                              <IconButton
                                size="small"
                                onClick={() => handleCopy(acc.phoneNumber ? acc.phoneNumber.replace(/^\+91/, '') : '', '10-Digit Phone Number')}
                                sx={{ color: '#9ca3af', '&:hover': { color: '#52ad83' } }}
                              >
                                <ContentCopy sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={acc.otp}
                              size="small"
                              sx={{
                                fontFamily: 'monospace',
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                background: 'rgba(82, 173, 131, 0.12)',
                                color: '#2d6d4b',
                                border: '1px solid rgba(82, 173, 131, 0.3)',
                                px: 0.5,
                              }}
                            />
                            <Tooltip title="Copy OTP">
                              <IconButton
                                size="small"
                                onClick={() => handleCopy(acc.otp, 'OTP')}
                                sx={{ color: '#9ca3af', '&:hover': { color: '#52ad83' } }}
                              >
                                <ContentCopy sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {acc.description || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Switch
                              size="small"
                              checked={acc.isActive}
                              onChange={() => handleToggleStatus(acc)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#52ad83' },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: '#52ad83',
                                },
                              }}
                            />
                            <Chip
                              label={acc.isActive ? 'Active' : 'Disabled'}
                              size="small"
                              color={acc.isActive ? 'success' : 'default'}
                              variant={acc.isActive ? 'filled' : 'outlined'}
                              sx={{ fontSize: '0.75rem', height: 22 }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit OTP / Details">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEdit(acc)}
                              sx={{ color: '#6b7280', '&:hover': { color: '#52ad83' } }}
                            >
                              <Edit sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Test Account">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDelete(acc)}
                              sx={{ color: '#6b7280', '&:hover': { color: '#ef4444' } }}
                            >
                              <Delete sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredAccounts.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </Paper>

      {/* Dynamic Rule Info Card */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mt: 3,
          borderRadius: 3,
          border: '1px dashed rgba(82, 173, 131, 0.4)',
          background: '#f9fdfa',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <HelpOutline sx={{ color: '#52ad83', fontSize: 32 }} />
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1f2937' }}>
            Pro-Tip: On-Demand Dynamic Test Numbers
          </Typography>
          <Typography variant="body2" sx={{ color: '#4b5563' }}>
            Any test phone number starting with <code>100...</code> (e.g., <code>1009998877</code>) automatically works on-demand without prior registration. Its expected OTP is simply the <strong>last 6 digits of the phone number</strong> (e.g. <code>998877</code>).
          </Typography>
        </Box>
      </Paper>

      {/* Dialog: Add Test Account */}
      <Dialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Add New Test Account</DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Account Role</InputLabel>
                <Select
                  value={formData.role}
                  label="Account Role"
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  <MenuItem value="merchant">Merchant (Akros Pro)</MenuItem>
                  <MenuItem value="user">Consumer (Akros App)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Account Name"
                placeholder="e.g. Oasis Salon or QA User 1"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Mobile Number (10 Digits)"
                placeholder="e.g. 1000550099"
                onChange={(e) => {
                  let raw = e.target.value.trim();
                  if (raw.startsWith('+91')) raw = raw.slice(3);
                  else if (raw.startsWith('+')) raw = raw.slice(1);

                  const digits = raw.replace(/\D/g, '');
                  let cleanPhone = digits.slice(0, 10);
                  if (digits.length >= 12 && digits.startsWith('91')) {
                    cleanPhone = digits.slice(2, 12);
                  } else if (digits.length === 11 && digits.startsWith('0')) {
                    cleanPhone = digits.slice(1, 11);
                  }
                  setFormData({
                    ...formData,
                    phoneNumber: cleanPhone,
                  });
                }}
                helperText="Enter 10-digit mobile number"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Fixed OTP"
                placeholder="e.g. 6-digit code"
                value={formData.otp}
                onChange={(e) => setFormData({ ...formData, otp: e.target.value })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Generate Random 6-Digit OTP">
                        <IconButton size="small" onClick={generateRandomOtp} sx={{ color: '#52ad83' }}>
                          <Casino sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Description / Purpose (Optional)"
                placeholder="e.g. Staging QA, Automated Maestro Flow, App Review"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenAddDialog(false)} sx={{ color: '#6b7280' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitAdd}
            sx={{
              background: '#52ad83',
              borderRadius: 2,
              '&:hover': { background: '#3d8f64' },
            }}
          >
            Create Account
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Edit Test Account */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Edit Test Account</DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Mobile Number (10 Digits)"
                disabled
                value={formData.phoneNumber ? formData.phoneNumber.replace(/^\+91/, '') : ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Account Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Fixed OTP"
                value={formData.otp}
                onChange={(e) => setFormData({ ...formData, otp: e.target.value })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Generate Random 6-Digit OTP">
                        <IconButton size="small" onClick={generateRandomOtp} sx={{ color: '#52ad83' }}>
                          <Casino sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenEditDialog(false)} sx={{ color: '#6b7280' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitEdit}
            sx={{
              background: '#52ad83',
              borderRadius: 2,
              '&:hover': { background: '#3d8f64' },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Delete Confirmation */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#4b5563' }}>
            Are you sure you want to remove test account <strong>{selectedAccount?.name}</strong> (
            <code>{selectedAccount?.phoneNumber ? selectedAccount.phoneNumber.replace(/^\+91/, '') : ''}</code>)? This will stop bypassing OTP for this account.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDeleteDialog(false)} sx={{ color: '#6b7280' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            sx={{ borderRadius: 2 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
